import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Download, Receipt, CheckCircle, Warning, CreditCard, X,
  ArrowRight, CircleNotch, ShieldCheck, Wallet, Plus, ArrowsClockwise,
  UsersThree, ShareNetwork, Copy,
} from "@phosphor-icons/react";
import { custApi, CUST_API_BASE, CUST_TOKEN_KEY, formatErr } from "../../lib/customerApi";
import { MePageTitle, MeCard, MeSection, MeEmpty, MeStagger } from "./MeUI";

const STATUS_STYLE = {
  paid: "bg-[#FEE2E2] text-[#7F1D1D]",
  unpaid: "bg-[#F4C7B9] text-[#7A2E18]",
  draft: "bg-[#F9E4B7] text-[#7A5A11]",
  refunded: "bg-[#E5E2D9] text-[#5C5A55]",
  pending_verification: "bg-[#F9E4B7] text-[#7A5A11]",
};
const STATUS_LABEL = {
  paid: "Paid",
  unpaid: "Unpaid",
  draft: "Draft",
  refunded: "Refunded",
  pending_verification: "Verifying UPI",
};

function loadRzpScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function MeDues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);        // per-invoice pay
  const [payingAll, setPayingAll] = useState(false);      // bundle pay
  const [rzpMode, setRzpMode] = useState("test");
  const [chooserOpen, setChooserOpen] = useState(null);    // "all" | invoice-object
  const [wallet, setWallet] = useState({ balance: 0, min_recharge: 500 });
  const [walletBusy, setWalletBusy] = useState(false);
  const [myManual, setMyManual] = useState([]);
  const [referral, setReferral] = useState(null);

  const load = () =>
    Promise.all([
      custApi.get("/me/invoices").then((r) => setItems(r.data)),
      custApi.get("/me/wallet").then((r) => setWallet({
        balance: Number(r.data?.balance || 0),
        min_recharge: Number(r.data?.min_recharge || 500),
      })).catch(() => {}),
      custApi.get("/payments/razorpay/manual-upi/mine")
             .then((r) => setMyManual(r.data)).catch(() => {}),
    ])
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    custApi.get("/payments/razorpay/config").then((r) => setRzpMode(r.data?.mode || "test")).catch(() => {});
    custApi.get("/me/referral-link").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const unpaidItems = items.filter((i) => i.payment_status === "unpaid");
  const pendingItems = items.filter((i) => i.payment_status === "pending_verification");
  const totalDue = unpaidItems.reduce((s, i) => s + (i.total || 0), 0);
  const pendingTotal = pendingItems.reduce((s, i) => s + (i.total || 0), 0);

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem(CUST_TOKEN_KEY);
      const r = await fetch(`${CUST_API_BASE}/me/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ────────────────────────────────────────────────────────────
  // Razorpay flows — single invoice + bundle
  // ────────────────────────────────────────────────────────────
  const razorpayCheckout = async ({ endpoint, body, invId }) => {
    const ok = await loadRzpScript();
    if (!ok || !window.Razorpay) {
      toast.error("Could not load payment SDK. Check your connection.");
      return;
    }
    const { data: order } = await custApi.post(endpoint, body || {});
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Rescigo Health",
      description: `Invoice ${order.invoice_no}`,
      order_id: order.order_id,
      prefill: order.prefill || {},
      notes: order.notes || {},
      theme: { color: "#D91F1F" },
      modal: {
        ondismiss: () => {
          setPayingId(null); setPayingAll(false);
          toast.message("Payment window closed");
        },
      },
      handler: async (resp) => {
        try {
          await custApi.post("/payments/razorpay/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Payment successful");
          load();
        } catch (e) { toast.error(formatErr(e)); }
        finally { setPayingId(null); setPayingAll(false); }
      },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(`Payment failed: ${resp?.error?.description || "unknown"}`);
      setPayingId(null); setPayingAll(false);
    });
    rzp.open();
  };

  const payInvoiceRzp = async (inv) => {
    setPayingId(inv.id); setChooserOpen(null);
    try {
      await razorpayCheckout({
        endpoint: "/payments/razorpay/order",
        body: { invoice_id: inv.id },
        invId: inv.id,
      });
    } catch (e) { toast.error(formatErr(e)); setPayingId(null); }
  };
  const payAllRzp = async () => {
    setPayingAll(true); setChooserOpen(null);
    try {
      await razorpayCheckout({ endpoint: "/payments/razorpay/pay-all-order" });
    } catch (e) { toast.error(formatErr(e)); setPayingAll(false); }
  };

  // ────────────────────────────────────────────────────────────
  // Wallet pay — atomic debit + mark-paid
  // ────────────────────────────────────────────────────────────
  const payFromWallet = async ({ invoiceIds }) => {
    setWalletBusy(true);
    try {
      const r = await custApi.post("/me/invoices/pay-from-wallet",
                                     { invoice_ids: invoiceIds });
      toast.success(`Paid ₹${r.data.amount} · Wallet balance ₹${r.data.balance}`);
      setChooserOpen(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setWalletBusy(false); }
  };

  // Recharge → returns after credit; caller decides next step.
  const rechargeWallet = async (amount) => {
    const ok = await loadRzpScript();
    if (!ok || !window.Razorpay) {
      toast.error("Could not load payment SDK. Check your connection.");
      return false;
    }
    const { data: order } = await custApi.post(
      "/me/wallet/recharge/create-order", { amount },
    );
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Rescigo Wallet",
        description: `Wallet recharge · ₹${amount}`,
        order_id: order.order_id,
        prefill: order.customer || {},
        theme: { color: "#DC2626" },
        modal: { ondismiss: () => resolve(false) },
        handler: async (resp) => {
          try {
            const v = await custApi.post("/me/wallet/recharge/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setWallet((w) => ({ ...w, balance: v.data.balance }));
            toast.success(`Wallet recharged · new balance ₹${v.data.balance}`);
            resolve(true);
          } catch (e) { toast.error(formatErr(e)); resolve(false); }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "unknown"}`);
        resolve(false);
      });
      rzp.open();
    });
  };

  return (
    <div className="font-body" data-testid="me-dues">
      <MePageTitle
        eyebrow="Rescigo · Dues"
        title="Invoices & payments"
        subtitle="Pay in one shot or invoice-by-invoice — UPI, cards, wallet."
      />

      {/* Total owing banner + Pay full amount */}
      <MeSection className="mt-2">
        <div className={`rounded-3xl p-5 relative overflow-hidden shadow-[0_20px_50px_rgba(15,31,12,0.14)] ${
          totalDue > 0
            ? "bg-gradient-to-br from-[#F97316] via-[#EA580C] to-[#C2410C] text-white"
            : "bg-gradient-to-br from-[#7F1D1D] via-[#1B3B18] to-[#DC2626] text-white"
        }`} data-testid="me-total-due">
          <div className="absolute -top-14 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/80 font-bold">
              {totalDue > 0 ? "Total due" : "You're all clear"}
            </div>
            <div className="font-display font-black text-4xl mt-1 tracking-tight">₹{totalDue.toFixed(2)}</div>
            <div className="text-[12px] text-white/80 mt-1">
              {totalDue > 0
                ? `${unpaidItems.length} invoice${unpaidItems.length !== 1 ? "s" : ""} pending — clear it in one tap.`
                : "No pending dues at the moment."}
            </div>
            {totalDue > 0 && (
              <button
                data-testid="btn-pay-full"
                disabled={payingAll}
                onClick={() => setChooserOpen("all")}
                className="mt-4 h-12 px-6 rounded-full bg-white text-[#C2410C] text-[13px] font-bold inline-flex items-center gap-2 shadow-md active:scale-[0.97] transition-transform disabled:opacity-60">
                {payingAll ? <CircleNotch size={16} className="animate-spin"/> : <CreditCard size={16} weight="bold"/>}
                {payingAll ? "Opening…" : `Pay full ₹${totalDue.toFixed(2)}`}
                <ArrowRight size={14} weight="bold"/>
              </button>
            )}
          </div>
        </div>
      </MeSection>

      {pendingTotal > 0 && (
        <MeSection className="mt-4">
          <div className="rounded-2xl border border-[#FDE68A] bg-[#FEF3C7] p-4 flex items-start gap-3" data-testid="pending-verif-banner">
            <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] border border-[#FDE68A] flex items-center justify-center shrink-0">
              <ShieldCheck size={18} color="#B45309" weight="fill"/>
            </div>
            <div className="text-[13px] text-[#78350F]">
              <div className="font-display font-bold">₹{pendingTotal.toFixed(2)} under UPI verification</div>
              <div className="text-[11px] mt-0.5 text-[#92400E]">You submitted a UPI transaction ID; our team usually verifies within 4 working hours.</div>
            </div>
          </div>
        </MeSection>
      )}

      {/* Referral nudge — only when there are dues to offset */}
      {totalDue > 0 && referral?.url && (
        <ReferralNudge referral={referral}/>
      )}

      {loading && <div className="px-5 mt-4 text-xs text-[#9CA3AF]">Loading…</div>}
      {!loading && items.length === 0 && (
        <MeSection className="mt-4">
          <MeEmpty icon={Receipt} title="No invoices yet"
                    subtitle="Your invoices will appear here once your first pickup happens."/>
        </MeSection>
      )}

      <MeSection className="mt-4">
      <MeStagger className="space-y-2" stagger={40} testid="me-inv-list">
        {items.map((inv) => (
          <div key={inv.id} className="bg-white border border-green-900/5 rounded-2xl shadow-[0_8px_24px_rgba(15,31,12,0.04)] p-4" data-testid={`me-inv-${inv.id}`}>
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${inv.payment_status === "paid" ? "bg-[#FEE2E2]" : "bg-[#FEE7D5]"}`}>
                {inv.payment_status === "paid"
                  ? <CheckCircle size={18} color="#DC2626" weight="fill" />
                  : <Warning size={16} color="#C25D38" weight="fill" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-medium text-sm">{inv.invoice_no}</span>
                  <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[inv.payment_status] || STATUS_STYLE.unpaid}`}>
                    {STATUS_LABEL[inv.payment_status] || inv.payment_status}
                  </span>
                </div>
                <div className="text-xs text-[#5C5A55] mt-0.5">{inv.date}</div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="font-heading text-lg text-[#1A1A1A]">₹{(inv.total || 0).toFixed(2)}</div>
                  <span className="text-[10px] text-[#8A8780]">incl. GST</span>
                </div>
                {inv.payment_status === "unpaid" && (
                  <button
                    data-testid={`me-inv-pay-${inv.id}`}
                    disabled={payingId === inv.id}
                    onClick={() => setChooserOpen(inv)}
                    className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-medium disabled:opacity-60"
                  >
                    <CreditCard size={14} weight="bold" />
                    {payingId === inv.id ? "Opening…" : `Pay ₹${(inv.total || 0).toFixed(2)}`}
                  </button>
                )}
                {inv.payment_status === "pending_verification" && (
                  <div className="mt-2 text-[10px] text-[#7A5A18] flex items-center gap-1">
                    <ShieldCheck size={11}/> UPI txn under verification — you&apos;ll be notified once approved.
                  </div>
                )}
                {inv.payment_status === "paid" && (inv.razorpay_payment_id || inv.upi_txn_id) && (
                  <div className="mt-2 text-[10px] text-[#DC2626] font-mono break-all">
                    Paid · {inv.razorpay_payment_id || inv.upi_txn_id}
                  </div>
                )}
              </div>
              <button
                data-testid={`me-inv-pdf-${inv.id}`}
                onClick={() => downloadPdf(inv)}
                className="self-start p-2 rounded-md hover:bg-[#F7F6F2] text-[#DC2626]"
                title="Download PDF"
              ><Download size={16} /></button>
            </div>
          </div>
        ))}
      </MeStagger>
      </MeSection>

      {myManual.length > 0 && (
        <MeSection className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] font-bold mb-2">My UPI submissions</div>
          <div className="space-y-1.5">
            {myManual.slice(0, 5).map((m) => (
              <div key={m.id} className="bg-white border border-[#E5E2D9] rounded p-3 text-xs flex items-center justify-between">
                <div>
                  <div className="font-mono">{m.upi_txn_id}</div>
                  <div className="text-[10px] text-[#8A8780]">
                    ₹{Number(m.amount).toFixed(2)} · {new Date(m.submitted_at).toLocaleDateString()}
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded ${
                  m.status === "verified" ? STATUS_STYLE.paid :
                  m.status === "rejected" ? STATUS_STYLE.unpaid :
                  STATUS_STYLE.pending_verification
                }`}>{m.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </MeSection>
      )}

      <div className="text-[11px] text-center text-[#8A8780] pt-2 space-y-1">
        <div>Powered by Razorpay · UPI · Cards · Netbanking · Wallets</div>
        {rzpMode === "live" && (
          <div className="inline-block px-2 py-0.5 rounded bg-[#FBE7DD] text-[#7A2E18] text-[10px] uppercase tracking-[0.2em]" data-testid="rzp-live-badge">
            Live mode · real charges apply
          </div>
        )}
      </div>

      {/* Payment-method chooser modal */}
      {chooserOpen && (
        <PaymentMethodChooser
          target={chooserOpen}
          totalDue={totalDue}
          unpaidItems={unpaidItems}
          wallet={wallet}
          walletBusy={walletBusy}
          onClose={() => setChooserOpen(null)}
          onRazorpay={() => chooserOpen === "all" ? payAllRzp() : payInvoiceRzp(chooserOpen)}
          onWalletPay={payFromWallet}
          onRecharge={rechargeWallet}
        />
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════
function PaymentMethodChooser({ target, totalDue, unpaidItems, wallet, walletBusy, onClose, onRazorpay, onWalletPay, onRecharge }) {
  const isBundle = target === "all";
  const amount = isBundle ? totalDue : (target.total || 0);
  const invoiceLabel = isBundle
    ? `${unpaidItems.length} invoices`
    : target.invoice_no;
  const invoiceIds = isBundle ? unpaidItems.map(i => i.id) : [target.id];

  const balance = Number(wallet?.balance || 0);
  const shortfall = Math.max(0, Math.ceil(amount - balance));
  const canPayFromWallet = balance >= amount;
  const [rechargeOpen, setRechargeOpen] = useState(false);
  // Suggested recharge is the shortfall rounded up to the min recharge floor.
  const minRecharge = Number(wallet?.min_recharge || 500);
  const suggestedRecharge = Math.max(minRecharge, Math.ceil(shortfall / 100) * 100);
  const [rechargeAmt, setRechargeAmt] = useState(suggestedRecharge);
  const [rechargingThenPay, setRechargingThenPay] = useState(false);

  const rechargeThenPay = async () => {
    if (Number(rechargeAmt) < minRecharge) {
      toast.error(`Minimum recharge is ₹${minRecharge}`);
      return;
    }
    setRechargingThenPay(true);
    try {
      const ok = await onRecharge(Number(rechargeAmt));
      if (ok) {
        // wallet balance state was updated inside onRecharge — auto-pay now
        setRechargeOpen(false);
        await onWalletPay({ invoiceIds });
      }
    } finally { setRechargingThenPay(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
         onClick={onClose}
         data-testid="pay-method-chooser">
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 pb-8 relative max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100">
          <X size={16}/>
        </button>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8A8780]">Pay</div>
        <div className="font-heading text-2xl mt-1">₹{amount.toFixed(2)}</div>
        <div className="text-xs text-[#5C5A55]">{invoiceLabel}</div>

        {/* Wallet card — always visible on top */}
        <div className={`mt-5 rounded-xl border p-4 ${canPayFromWallet ? "border-emerald-300 bg-emerald-50" : "border-[#E5E2D9] bg-[#F7F6F2]"}`}
              data-testid="pm-wallet-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${canPayFromWallet ? "bg-emerald-600 text-white" : "bg-[#DC2626] text-white"}`}>
              <Wallet size={20} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Rescigo Wallet</div>
              <div className="font-heading text-xl leading-tight" data-testid="pm-wallet-balance">
                ₹{balance.toFixed(2)}
              </div>
              <div className={`text-[11px] mt-0.5 ${canPayFromWallet ? "text-emerald-700" : "text-[#7A2E18]"}`}>
                {canPayFromWallet
                  ? `You have enough to clear this in one tap.`
                  : `Short by ₹${shortfall.toFixed(2)} — recharge and pay in one flow.`}
              </div>
            </div>
          </div>

          {canPayFromWallet ? (
            <button
              data-testid="pm-wallet-pay"
              disabled={walletBusy}
              onClick={() => onWalletPay({ invoiceIds })}
              className="mt-3 w-full h-11 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {walletBusy ? <CircleNotch size={16} className="animate-spin"/> : <Wallet size={16} weight="fill"/>}
              {walletBusy ? "Paying…" : `Pay ₹${amount.toFixed(2)} from wallet`}
            </button>
          ) : rechargeOpen ? (
            <div className="mt-3 space-y-2.5" data-testid="pm-wallet-recharge-form">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-1">
                  Recharge amount (min ₹{minRecharge})
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780] text-sm">₹</span>
                    <input
                      data-testid="pm-recharge-amount"
                      type="number"
                      min={minRecharge}
                      step={100}
                      value={rechargeAmt}
                      onChange={(e) => setRechargeAmt(e.target.value)}
                      className="w-full h-11 pl-7 pr-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
                    />
                  </div>
                  <button
                    data-testid="pm-recharge-cancel"
                    type="button"
                    onClick={() => setRechargeOpen(false)}
                    className="h-11 px-3 border border-[#E5E2D9] rounded-md text-xs text-[#5C5A55]">
                    Cancel
                  </button>
                </div>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {[suggestedRecharge, suggestedRecharge + 500, suggestedRecharge + 1000].map((v) => (
                    <button key={v} type="button" onClick={() => setRechargeAmt(v)}
                             data-testid={`pm-recharge-quick-${v}`}
                             className={`h-7 px-2.5 rounded-full border text-[11px] ${Number(rechargeAmt) === v ? "border-[#DC2626] bg-[#FEE2E2] text-[#7A2E18]" : "border-[#E5E2D9] bg-white text-[#5C5A55]"}`}>
                      ₹{v}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-[#8A8780] mt-1.5">
                  You&apos;ll be charged ₹{Number(rechargeAmt || 0).toFixed(2)} via Razorpay. Once the wallet is topped up,
                  we&apos;ll auto-pay ₹{amount.toFixed(2)} for {invoiceLabel} — no extra tap needed.
                </div>
              </div>
              <button
                data-testid="pm-recharge-and-pay"
                disabled={rechargingThenPay}
                onClick={rechargeThenPay}
                className="w-full h-11 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {rechargingThenPay ? <CircleNotch size={16} className="animate-spin"/> : <ArrowsClockwise size={16} weight="bold"/>}
                {rechargingThenPay ? "Processing…" : `Recharge ₹${Number(rechargeAmt || 0).toFixed(2)} & pay`}
              </button>
            </div>
          ) : (
            <button
              data-testid="pm-wallet-recharge-open"
              onClick={() => setRechargeOpen(true)}
              className="mt-3 w-full h-11 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2] text-sm font-semibold inline-flex items-center justify-center gap-2">
              <Plus size={16} weight="bold"/>
              Recharge & Pay
            </button>
          )}
        </div>

        {/* Razorpay — always available */}
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] font-semibold">
            Or pay directly
          </div>
          <MethodBtn
            testid="pm-razorpay"
            onClick={onRazorpay}
            icon={<CreditCard size={18} weight="fill" color="#0B2B66"/>}
            title="Razorpay · UPI · Cards · Netbanking · Wallets"
            subtitle="Instant · auto-verified"
            highlight/>
        </div>
      </div>
    </div>
  );
}

function MethodBtn({ onClick, icon, title, subtitle, highlight, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`w-full flex items-center gap-3 text-left p-4 rounded-lg border transition ${
              highlight
                ? "border-[#DC2626] bg-[#EFF3EE] hover:bg-[#DCEDD3]"
                : "border-[#E5E2D9] bg-white hover:bg-slate-50"
            }`}>
      <div className="w-10 h-10 rounded-md bg-white ring-1 ring-[#E5E2D9] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="text-[11px] text-[#5C5A55] mt-0.5">{subtitle}</div>
      </div>
      <ArrowRight size={14} className="text-[#8A8780]"/>
    </button>
  );
}


// ═════════════════════════════════════════════════════════════
// Referral nudge — invite friends → get up to ₹100 wallet credit
// Reward is 10% of the referred customer's first Rescigo Package
// invoice, capped at ₹100 per invite.
// ═════════════════════════════════════════════════════════════
function ReferralNudge({ referral }) {
  const url = referral?.url;
  const CAP = 100;

  const share = async () => {
    if (!url) return;
    const text = `I use Rescigo Health for daily waste + family care. Sign up with my link and we both save on our next Rescigo Package. ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Rescigo Health", text, url }); return; }
      catch { /* fell through to copy */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere"); }
    catch { toast.message(url); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — long-press to select"); }
  };

  return (
    <MeSection className="mt-4">
      <div className="relative overflow-hidden rounded-2xl border border-[#BBF7D0] bg-gradient-to-br from-[#DCFCE7] via-[#F0FDF4] to-white p-4 shadow-[0_8px_20px_rgba(21,128,61,0.08)]"
            data-testid="dues-referral-nudge">
        <div className="absolute -top-8 -right-6 w-32 h-32 rounded-full bg-emerald-200/40 blur-2xl pointer-events-none"/>
        <div className="relative flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shrink-0 shadow-md">
            <UsersThree size={20} weight="fill"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">
              Invite &amp; earn · offset this bill
            </div>
            <div className="font-heading text-[15px] leading-tight text-[#111827] mt-0.5">
              Invite a friend, get up to <span className="text-emerald-700">₹{CAP}</span> wallet credit
            </div>
            <p className="text-[11px] text-[#374151] mt-1 leading-snug">
              Reward is credited when your friend <span className="font-semibold">registers</span> and
              books a <span className="font-semibold">Rescigo Package Service</span>. Use it to pay down
              this bill or any future invoice.
            </p>
            <div className="mt-2.5 flex items-center gap-2">
              <div className="flex-1 min-w-0 font-mono text-[11px] bg-white border border-emerald-200 rounded-lg px-2.5 py-1.5 truncate"
                    data-testid="dues-referral-link">
                {url}
              </div>
              <button type="button" onClick={copy}
                       data-testid="dues-referral-copy"
                       className="h-9 px-2.5 rounded-lg bg-white border border-emerald-300 text-emerald-700 text-[11px] font-bold inline-flex items-center gap-1">
                <Copy size={12}/> Copy
              </button>
              <button type="button" onClick={share}
                       data-testid="dues-referral-share"
                       className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold inline-flex items-center gap-1">
                <ShareNetwork size={12} weight="fill"/> Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </MeSection>
  );
}
